import { Building2, Search, UserPlus } from "lucide-react";
import { Button } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import type { InviteStatus } from "@/features/identity-hub/api/identity-hub.api";
import { StatusChip } from "./shared/toolbar/StatusChip";
import { ClearFilters } from "./shared/toolbar/ClearFilters";
import { FilterSelectChip } from "./shared/toolbar/FilterSelectChip";

// English fallbacks per key — react-i18next picks these up via `defaultValue`,
// so the UI never shows raw key paths even before the locale bundle resolves.
const TOOLBAR_FALLBACKS: Record<string, string> = {
  searchPlaceholder: "Search by email, sender, or org…",
  statusAll:         "All",
  statusPending:     "Pending",
  statusExpired:     "Expired",
  statusAccepted:    "Accepted",
  filterOrg:         "Organization",
  filterOrgAll:      "All organizations",
  clearFilters:      "Clear filters",
  invite:            "Invite person",
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InvitesFilters {
  search: string;
  status: InviteStatus | "all";
  tenantId: string | null;
}

interface InvitesToolbarProps {
  filters: InvitesFilters;
  onFilterChange: <K extends keyof InvitesFilters>(key: K, value: InvitesFilters[K]) => void;
  orgs: Array<{ id: string; name: string }>;
  onInvite?: () => void;
  canInvite?: boolean;
  /** Counts per-status keyed off the live page, used to label the chips. */
  counts?: Partial<Record<InviteStatus | "all", number>>;
}

// ─── InvitesToolbar ──────────────────────────────────────────────────────────

export function InvitesToolbar({
  filters,
  onFilterChange,
  orgs,
  onInvite,
  canInvite = true,
  counts,
}: InvitesToolbarProps) {
  const { t } = useTranslation();
  const tb = (key: string) =>
    t(`iam.invites.toolbar.${key}`, {
      defaultValue: TOOLBAR_FALLBACKS[key] ?? key,
    });

  const activeOrgName = orgs.find((o) => o.id === filters.tenantId)?.name;
  const hasFilters = filters.status !== "all" || !!filters.tenantId || !!filters.search;

  const clearAll = () => {
    onFilterChange("search", "");
    onFilterChange("status", "all");
    onFilterChange("tenantId", null);
  };

  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 py-3">
      {/* Search — fills the row next to the action on mobile; fixed-width on sm+ */}
      <label className="relative order-1 flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-muted-foreground focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40 sm:flex-none">
        <Search className="h-3.5 w-3.5 shrink-0" />
        <input
          type="search"
          placeholder={tb("searchPlaceholder")}
          value={filters.search}
          onChange={(e) => onFilterChange("search", e.target.value)}
          className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70 sm:w-56 sm:flex-none"
        />
      </label>

      {/* Filters — own full-width row on mobile; inline on sm+ */}
      <div className="order-3 flex w-full flex-wrap items-center gap-2.5 sm:order-2 sm:w-auto sm:flex-1">
      {/* Status chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusChip
          label={tb("statusAll")}
          count={counts?.all}
          active={filters.status === "all"}
          onClick={() => onFilterChange("status", "all")}
        />
        <StatusChip
          label={tb("statusPending")}
          count={counts?.pending}
          active={filters.status === "pending"}
          onClick={() => onFilterChange("status", "pending")}
          tone="amber"
        />
        <StatusChip
          label={tb("statusExpired")}
          count={counts?.expired}
          active={filters.status === "expired"}
          onClick={() => onFilterChange("status", "expired")}
          tone="rose"
        />
        <StatusChip
          label={tb("statusAccepted")}
          count={counts?.accepted}
          active={filters.status === "accepted"}
          onClick={() => onFilterChange("status", "accepted")}
          tone="emerald"
        />
      </div>

      {/* Org filter */}
      <FilterSelectChip
        icon={<Building2 className="h-3 w-3 shrink-0" />}
        label={activeOrgName ?? tb("filterOrg")}
        active={!!filters.tenantId}
      >
        <select
          value={filters.tenantId ?? ""}
          onChange={(e) => onFilterChange("tenantId", e.target.value || null)}
          className="absolute inset-0 cursor-pointer opacity-0"
        >
          <option value="">{tb("filterOrgAll")}</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </FilterSelectChip>

      {hasFilters && <ClearFilters label={tb("clearFilters")} onClick={clearAll} />}
      </div>

      {/* Primary action — stays on the search row on mobile; at the end on sm+ */}
      {canInvite && onInvite && (
        <div className="order-2 shrink-0 sm:order-3 sm:ml-auto">
          <Button variant="default" size="sm" className="gap-1.5" onClick={onInvite}>
            <UserPlus className="h-3.5 w-3.5" />
            {tb("invite")}
          </Button>
        </div>
      )}
    </div>
  );
}
